import { getHttpErrorStatus } from './get-http-error-status';

const NUMERIC_ID_RE = /\/\d+(?=\/|$)/g;
const UUID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

export type AxiosSentryContext = {
  fingerprint: string[];
  tags: Record<string, string>;
};

export function getAxiosSentryContext(
  error: unknown,
): AxiosSentryContext | null {
  if (!isAxiosLikeError(error)) {
    return null;
  }

  const errorClass = getAxiosErrorClass(error);
  const method = getAxiosMethod(error);
  const endpoint = getNormalizedAxiosEndpoint(error);
  const code = getAxiosCode(error);
  const status = getHttpErrorStatus(error);

  const fingerprint =
    errorClass.startsWith('http-') && endpoint
      ? ['axios', errorClass, `${method} ${endpoint}`]
      : ['axios', errorClass];

  const tags: Record<string, string> = {};

  if (endpoint) {
    tags['api.endpoint'] = `${method} ${endpoint}`;
  }

  if (code) {
    tags['axios.code'] = code;
  }

  tags['axios.error_class'] = errorClass;

  if (status !== null && status > 0) {
    tags['http.status_code'] = String(status);
  }

  return {
    fingerprint,
    tags,
  };
}

export function isCanceledAxiosError(error: unknown): boolean {
  return isAxiosLikeError(error) && getAxiosErrorClass(error) === 'aborted';
}

function isAxiosLikeError(error: unknown): error is Record<string, unknown> {
  if (!error || typeof error !== 'object') {
    return false;
  }

  if ('isAxiosError' in error && error.isAxiosError === true) {
    return true;
  }

  return 'name' in error && error.name === 'AxiosError';
}

function getAxiosErrorClass(error: Record<string, unknown>): string {
  const status = getHttpErrorStatus(error);
  if (status !== null && status > 0) {
    return `http-${status}`;
  }

  const code = getAxiosCode(error);
  const message = getErrorMessage(error);

  if (
    code === 'ERR_CANCELED' ||
    message === 'canceled' ||
    message === 'Request aborted'
  ) {
    return 'aborted';
  }

  if (
    code === 'ECONNABORTED' ||
    message === 'timeout exceeded' ||
    message.startsWith('timeout of ')
  ) {
    return 'timeout';
  }

  if (code === 'ERR_NETWORK' || message === 'Network Error') {
    return 'network';
  }

  return code ?? 'unknown';
}

function getAxiosCode(error: Record<string, unknown>): string | null {
  return typeof error.code === 'string' && error.code ? error.code : null;
}

function getErrorMessage(error: Record<string, unknown>): string {
  return typeof error.message === 'string' ? error.message : '';
}

function getAxiosMethod(error: Record<string, unknown>): string {
  const config = getAxiosConfig(error);
  const method =
    config && typeof config.method === 'string' ? config.method : '';

  return (method || 'get').toUpperCase();
}

function getNormalizedAxiosEndpoint(
  error: Record<string, unknown>,
): string | null {
  const config = getAxiosConfig(error);
  const rawUrl = config && typeof config.url === 'string' ? config.url : null;

  if (!rawUrl) {
    return null;
  }

  const path = rawUrl.split('?')[0]?.replace(/^https?:\/\/[^/]+/i, '') ?? '';
  const withLeadingSlash = path.startsWith('/') ? path : `/${path}`;

  return withLeadingSlash
    .replace(UUID_RE, ':id')
    .replace(NUMERIC_ID_RE, '/:id');
}

function getAxiosConfig(
  error: Record<string, unknown>,
): Record<string, unknown> | null {
  if ('config' in error && error.config && typeof error.config === 'object') {
    return error.config as Record<string, unknown>;
  }

  return null;
}
